import React, { useEffect, useMemo } from 'react';
import PropTypes, { InferProps } from 'prop-types';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useNavigate } from 'react-router-dom';

import classnames from 'classnames/bind';

import { notify } from '@NOTIFICATION/Notificator';

import ROUTES from '@CONSTANTS/ROUTES.constant';

import {
    AdminSingleChapterData,
    AdminSingleChapterDataSection,
    AdminSingleChapterMeta,
} from '@INTERFACES/admin-api/chapters';

import { useCopySection } from '@SERVICES';

import useDeleteSectionModal from '@HOOKS/store/modals/useDeleteSectionModal';
import useDocumentPermissions from '@HOOKS/useDocumentPermissions';
import useNewVariantModal from '@HOOKS/store/modals/useNewVariantModal';
import usePreviewSectionModal from '@HOOKS/store/modals/usePreviewSectionModal';
import useSectionHistoryModal from '@HOOKS/store/modals/useSectionHistoryModal';

import TripleCardButtons from '@COMPONENTS/COMMON/buttons/TripleCardButtons';
import RoundIconButton from '@COMPONENTS/COMMON/buttons/RoundIconButton';
import AddButton from '@COMPONENTS/COMMON/buttons/AddButton';
import Warning from '@COMPONENTS/SHARED/Warning';

import styles from './SectionCard.module.scss';

const cx: CX = classnames.bind(styles);

function SectionCard(props: Props) {
    const {
        section,
        chapterData,
        chapterMeta,
        index,
    } = props;

    const {
        canEditDocument,
        canGenerateDocument,
    } = useDocumentPermissions({
        documentOwnerId: chapterData.document.owner,
        permissions: chapterMeta.permissions,
    });

    const [
        copySection,
        {
            isLoading: isCopyLoading,
            isSuccess: isCopySuccess,
            isError: isCopyError,
            error: copyError,
        },
    ] = useCopySection();

    const { actions: deleteSectionActions } = useDeleteSectionModal(() => true);
    const { actions: previewSectionModalActions } = usePreviewSectionModal(() => true);
    const { actions: sectionHistoryModalActions } = useSectionHistoryModal(() => true);
    const { actions: newVariantModalActions } = useNewVariantModal(() => true);

    const navigate = useNavigate();

    const isPublished = useMemo(() => (
        section.variants.every((variant) => variant.isPublished)
        && section.baseText.isPublished
    ), [section]);

    useEffect(() => {
        if (isCopySuccess) {
            notify.info('Section has been copied', {
                toastId: 'copy-section',
            });
        }
    }, [isCopySuccess]);

    useEffect(() => {
        if (isCopyError) {
            if ((copyError as FetchBaseQueryError).status === 403) {
                notify.error('Permission denied', {
                    toastId: 'copy-section-permission-error',
                });
            } else {
                notify.error('Something wrong!', {
                    toastId: 'copy-section-error',
                });
            }
        }
    }, [isCopyError, copyError]);

    const handleNavigateToSection = () => {
        navigate(ROUTES.admin.sections.id.get(section.id));
    };

    const handleNavigateToVariant = (variantId: number) => {
        // Manually construct the URL with proper hash format to ensure direct variant navigation
        const sectionUrl = ROUTES.admin.sections.id.get(section.id);
        const targetUrl = `${sectionUrl}#variant-${variantId}`;
        navigate(targetUrl);
    };

    const handleAddNewVariant = () => {
        newVariantModalActions.open({
            documentId: chapterData.document.id,
            chapterId: chapterData.id,
            sectionId: section.id,
            canEditDocument,
        });
    };

    function getBaseTextWarning() {
        if (section.baseText.latestVersion === null) {
            return 'Section base text not published';
        }
        return 'Section base text has unpublished changes';
    }

    const getVariantStatusText = (variant: any) => (
        !variant.isPublished ? 'Draft' : 'Published'
    );

    const getVariantStatusClass = (variant: any) => (
        !variant.isPublished ? 'draft' : 'published'
    );

    return (
        <div className={cx('section-card')}>
            <div className={cx('section-info-wrapper')}>
                {!isPublished && <div className={cx('accent')} />}

                <div className={cx('header')}>
                    <div className={cx('section-number')}>
                        {`Section ${index}`}
                    </div>
                    <div className={cx('header-actions')}>
                        <TripleCardButtons
                            locked={{
                                edit: false,
                                copy: !canEditDocument,
                                delete: !canEditDocument,
                            }}
                            processing={{
                                edit: false,
                                copy: isCopyLoading,
                                delete: false,
                            }}
                            tooltips={{
                                edit: 'Edit section',
                                copy: 'Copy section',
                                delete: 'Delete section',
                            }}
                            onEdit={handleNavigateToSection}
                            onCopy={() => {
                                copySection({
                                    documentId: chapterData.document.id,
                                    chapterId: chapterData.id,
                                    id: section.id,
                                });
                            }}
                            onDelete={() => {
                                deleteSectionActions.open({
                                    id: section.id,
                                    name: section.name,
                                    documentId: chapterData.document.id,
                                    chapterId: chapterData.id,
                                });
                            }}
                        />
                        <div className={cx('additional-actions')}>
                            <RoundIconButton
                                tooltipTitle="Section preview"
                                icon="preview"
                                onClick={() => {
                                    previewSectionModalActions.open({
                                        documentId: chapterData.document.id,
                                        sectionId: section.id,
                                        sections: chapterData.sections,
                                        canGenerateDocument,
                                    });
                                }}
                            />
                            <RoundIconButton
                                tooltipTitle="Section history"
                                icon="history"
                                onClick={() => {
                                    sectionHistoryModalActions.open({
                                        sectionId: section.id,
                                        sections: chapterData.sections,
                                    });
                                }}
                            />
                        </div>
                    </div>
                </div>

                <div className={cx('title')}>{section.name}</div>
            </div>

            <div className={cx('variants-section')}>
                {/* Base text warning if needed */}
                {!section.baseText.isPublished && (
                    <div className={cx('base-text-warning')}>
                        <Warning text={getBaseTextWarning()} />
                    </div>
                )}

                <div className={cx('variants-header')}>
                    <h4 className={cx('variants-title')}>
                        Variants ({section.variants.length})
                    </h4>
                    <div className={cx('add-variant-btn')}>
                        <AddButton
                            locked={!canEditDocument}
                            title="New variant"
                            fontSize={12}
                            size="small"
                            onClick={handleAddNewVariant}
                        />
                    </div>
                </div>

                {section.variants.length > 0 ? (
                    <div className={cx('variants-list')}>
                        {section.variants.map((variant) => (
                            <div
                                key={variant.id}
                                className={cx('variant-item', {
                                    'is-draft': !variant.isPublished,
                                })}
                                onClick={() => handleNavigateToVariant(variant.id)}
                                role="button"
                                tabIndex={0}
                            >
                                <div className={cx('variant-info')}>
                                    <div className={cx('variant-name')}>
                                        {variant.isDefault 
                                            ? 'Default Variant' 
                                            : (variant.name || `Variant ${variant.id}`)}
                                    </div>
                                    <div className={cx('variant-meta')}>
                                        <span className={cx('variant-status', getVariantStatusClass(variant))}>
                                            {getVariantStatusText(variant)}
                                        </span>
                                        {variant.isDefault && (
                                            <span className={cx('default-badge')}>
                                                Default
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className={cx('variant-actions')}>
                                    <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <polyline points="9,18 15,12 9,6" />
                                    </svg>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className={cx('no-variants')}>
                        <div className={cx('no-variants-content')}>
                            <div className={cx('no-variants-icon')}>
                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                    <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
                                    <polyline points="14,2 14,8 20,8" />
                                    <line x1="16" y1="13" x2="8" y2="13" />
                                    <line x1="16" y1="17" x2="8" y2="17" />
                                    <polyline points="10,9 9,9 8,9" />
                                </svg>
                            </div>
                            <h5 className={cx('no-variants-title')}>No Variants</h5>
                            <p className={cx('no-variants-description')}>
                                This section doesn&apos;t have any variants yet. Create your first variant to get started.
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

SectionCard.propTypes = {
    index: PropTypes.string.isRequired,
};

type Props = InferProps<typeof SectionCard.propTypes> & {
    section: AdminSingleChapterDataSection;
    chapterData: AdminSingleChapterData;
    chapterMeta: AdminSingleChapterMeta;
};

export default SectionCard; 